import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Trans} from '@lingui/macro';
import cls from 'classnames';

import FlatButton from '../../FlatButton';
import ChevronRightIcon from '@components/Icons/ChevronRightIcon';
import CompletedIcon from '@components/Icons/CompletedIcon';

import useMediaQuery from '../../../hooks/useMediaQuery';

import s from './ClosedQuestion.css';

export default function ClosedQuestion(props) {
  const {total, count, options, question, onSelect, totalPage, currPage} = props;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Mobile view for screens narrower than 768px
  const isMobile = useMediaQuery('(max-width: 900px)');

  const hasReducedOptions = options?.length < 5;

  const handleNext = () => {
    if (currentQuestionIndex < question?.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };
  return (
    <div className={s.ClosedQuestion}>
      <div className={cls(s.Flex, s.QuestionHeader)}>
        <div className={cls(s.Flex, s.QuestionCounter)}>
          <div className={s.Text}>
            <Trans id="closedQuestion.title" components={[<br />]} />
          </div>
          <div className={s.Counter}>
            <sup>{currPage}</sup>
            <span>/</span>
            <sub>{totalPage}</sub>
            <span className="visually-hidden">
              <Trans id="closedQuestion.options_label">Opzioni Possibili:</Trans>
              <Trans id="closedQuestion.radio_instruction">premi spazio per selezionare</Trans>
            </span>
          </div>
        </div>

        {!isMobile && (
          <div className={cls(s.Flex, s.QuestionOptions, hasReducedOptions && s.ReducedOptions)}>
            {options?.map(op => (
              <div key={op.value}>{op.option}</div>
            ))}
          </div>
        )}
      </div>
      {isMobile ? (
        <MobileQuestionView
          question={question}
          options={options}
          currentQuestionIndex={currentQuestionIndex}
          onSelect={onSelect}
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          count={count}
        />
      ) : (
        <DesktopQuestionView
          question={question}
          options={options}
          hasReducedOptions={hasReducedOptions}
          onSelect={onSelect}
          count={count}
        />
      )}
    </div>
  );
}

const MobileQuestionView = ({
  question,
  options,
  currentQuestionIndex,
  onSelect,
  handleNext,
  handlePrevious,
  count,
}) => {
  // Calculate the actual question number based on count
  // count is the total questions answered so far, so we need to calculate the offset
  const questionsBeforeThisPage = count - question.length;
  const currentQuestionNumber = questionsBeforeThisPage + currentQuestionIndex + 1;

  return (
    <div className={s.MobileQuestionContainer}>
      {question && question.length > 0 && (
        <>
          <div className={s.MobileQuestionProgress}>
            <FlatButton
              className={cls(s.NavButton, s.PrevButton)}
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
            >
              <ChevronRightIcon className={cls(s.Icon, s.IconPrevious)} />
              <span>
                <Trans id="question.topbar.previous">Domanda precedente</Trans>
              </span>
            </FlatButton>

            <FlatButton
              className={cls(s.NavButton, s.NextButton)}
              onClick={handleNext}
              disabled={currentQuestionIndex === question.length - 1}
            >
              <span>
                <Trans id="question.topbar.next">Domanda successiva</Trans>
              </span>
              <ChevronRightIcon className={s.Icon} />
            </FlatButton>
          </div>
          <div
            role="group"
            aria-labelledby={`question-${question[currentQuestionIndex].id}`}
            className={cls(s.Flex, s.MobileQuestionWrapper)}
          >
            <h4
              id={`question-${question[currentQuestionIndex].id}`}
              className={s.Question}
              style={{font: 'inherit', margin: 0, fontWeight: 'inherit'}}
            >
              <span className="visually-hidden">
                <Trans id="closedQuestion.question_label">Domanda</Trans>
              </span>
              <strong>{currentQuestionNumber}.</strong> {question[currentQuestionIndex].text}
            </h4>
          </div>
          <div className={s.MobileOptionsList}>
            {options.map((op, answerIndex) => (
              <div key={answerIndex} className={s.MobileOptionItem}>
                <label
                  className={cls(
                    s.MobileOptionLabel,
                    op.value === question[currentQuestionIndex].value && s.SelectedOption
                  )}
                  htmlFor={`question-${question[currentQuestionIndex].id}-${answerIndex}`}
                >
                  <span className={s.OptionText}>
                    {op.option}
                    <span className="visually-hidden">,</span>
                  </span>
                  {op.value === question[currentQuestionIndex].value && (
                    <div className={s.Checkmark}>
                      <CompletedIcon className={s.CheckImage} />
                    </div>
                  )}
                  <input
                    id={`question-${question[currentQuestionIndex].id}-${answerIndex}`}
                    type="radio"
                    name={`answer-${question[currentQuestionIndex].id}`}
                    value={op.value}
                    checked={op.value === question[currentQuestionIndex].value}
                    onChange={() =>
                      onSelect({
                        questionId: question[currentQuestionIndex].id,
                        value: op.value,
                      })
                    }
                  />
                </label>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Desktop view component
const DesktopQuestionView = ({question, options, hasReducedOptions, onSelect, count}) => {
  // Calculate the offset for questions before this page
  const questionsBeforeThisPage = count - question.length;

  return (
    <div className={s.QuestionContainer}>
      {question?.map((q, index) => {
        const questionNumber = questionsBeforeThisPage + index + 1;
        return (
          <div
            key={q.id}
            role="group"
            aria-labelledby={`question-${q.id}`}
            className={cls(s.Flex, s.QuestionWrapper)}
          >
            <h4
              id={`question-${q.id}`}
              className={s.Question}
              style={{font: 'inherit', margin: 0, fontWeight: 'inherit'}}
            >
              <span className="visually-hidden">
                <Trans id="closedQuestion.question_label">Domanda</Trans>
              </span>
              <strong>{questionNumber}.</strong> {q.text}
            </h4>{' '}
            <div className={cls(s.Flex, s.QuestionOptions, hasReducedOptions && s.ReducedOptions)}>
              {options.map((op, answerIndex) => (
                <React.Fragment key={answerIndex}>
                  <label className="visually-hidden" htmlFor={`question-${q.id}-${answerIndex}`}>
                    {op.option},
                    <Trans id="closedQuestion.radio_instruction">
                      premi spazio per selezionare
                    </Trans>
                  </label>
                  <label className={s.radioWrapper}>
                    <input
                      id={`question-${q.id}-${answerIndex}`}
                      type="radio"
                      name={`answer-${q.id}`}
                      value={op.value}
                      checked={op.value === q.value}
                      onClick={() => onSelect({questionId: q.id, value: op.value})}
                    />
                  </label>
                </React.Fragment>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

ClosedQuestion.propTypes = {
  total: PropTypes.number,
  count: PropTypes.number,
  options: PropTypes.array,
  question: PropTypes.array,
  onSelect: PropTypes.func,
  totalPage: PropTypes.number,
  currPage: PropTypes.number,
};

MobileQuestionView.propTypes = {
  question: PropTypes.array,
  options: PropTypes.array,
  currentQuestionIndex: PropTypes.number,
  onSelect: PropTypes.func,
  handleNext: PropTypes.func,
  handlePrevious: PropTypes.func,
  count: PropTypes.number,
};

DesktopQuestionView.propTypes = {
  question: PropTypes.array,
  options: PropTypes.array,
  hasReducedOptions: PropTypes.bool,
  onSelect: PropTypes.func,
  count: PropTypes.number,
};
